import { Box, Typography, colorsPalette, styled } from '@lingoda/ui';
import { CheckIcon, CircleIcon } from '@lingoda/ui/icons/lingodaIcons';

type ChecklistStepItem = {
    stepNumber: number;
    isCompleted: boolean;
    isActive: boolean;
    title: string;
};

interface Props {
    step: ChecklistStepItem;
}

export const ChecklistStepItem = ({ step }: Props) => {
    const activeStepTypography = step.isActive ? 'subtitle1' : 'body2';

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" height={32}>
                <Box display="flex" alignItems="center" justifyContent="center" width={32} mr={2}>
                    <StyledStepNumber completed={step.isCompleted} active={step.isActive}>
                        <Typography variant={activeStepTypography}>{step.stepNumber}</Typography>
                    </StyledStepNumber>
                </Box>

                <StyledChecklistStepTitle
                    variant={activeStepTypography}
                    completed={step.isCompleted}
                >
                    {step.title}
                </StyledChecklistStepTitle>
            </Box>
            <StepStatusIcon isCompleted={step.isCompleted} />
        </Box>
    );
};

const StepStatusIcon = ({ isCompleted }: { isCompleted: boolean }) => {
    if (isCompleted) {
        return (
            <StatusIcon>
                <CheckIcon size={16} />
            </StatusIcon>
        );
    }

    return <CircleIcon size={24} weight="bold" color={colorsPalette.blackAlpha[16]} />;
};

const StyledStepNumber = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'completed' && prop !== 'active',
})<{ completed: boolean; active: boolean }>(({ theme, completed, active }) => ({
    height: theme.spacing(3),
    width: theme.spacing(3),
    borderRadius: '50%',
    backgroundColor: colorsPalette.primarySolid[8],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: colorsPalette.primarySolid[70],
    ...(active && {
        height: theme.spacing(4),
        width: theme.spacing(4),
        backgroundColor: colorsPalette.primarySolid[20],
        color: theme.palette.primary.main,
    }),
    ...(completed && {
        backgroundColor: colorsPalette.blackSolid[8],
        color: colorsPalette.blackSolid[70],
    }),
}));

const StyledChecklistStepTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'completed',
})<{ completed: boolean }>(({ theme, completed }) => ({
    lineHeight: theme.spacing(4),
    ...(completed && {
        textDecoration: 'line-through',
    }),
}));

const StatusIcon = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    background: theme.colors.border['progress-bold'],
    color: theme.colors.text.inverse,
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
}));
